<template>
    <div class="mt-2">
        <div>
            <div class="flex justify-between items-center mb-6">
                <div>
                    <h1 class="text-2xl font-bold text-gray-800 dark:text-white">Membros do Setor: {{ sectorName }}</h1>
                    <p class="text-gray-600 dark:text-gray-400 mt-1">Gerenciamento de membros deste setor</p>
                </div>
                <button 
                    @click="goBack" 
                    class="bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded-md flex items-center"
                >
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                    </svg>
                    Voltar
                </button>
            </div>
            
            <!-- Busca e adição de membros -->
            <div class="mb-6 bg-gray-100 dark:bg-gray-900 p-4 rounded-lg">
                <h2 class="text-lg font-semibold mb-4 text-gray-800 dark:text-white">Adicionar Membro</h2>
                <div class="flex flex-col gap-4">
                    <div class="flex-grow" v-if="!selectedUser">
                        <users-select
                            v-model="selectedUser"
                            :multiple="false"
                            :return-object="true"
                            class="dark:text-white"
                        />
                    </div>
                    <div v-if="selectedUser" class="mt-2">
                        <div class="bg-gray-200 dark:bg-gray-600 p-3 rounded-md">
                            <div class="flex justify-between items-center">
                                <div>
                                    <div class="font-medium text-gray-800 dark:text-white">{{ selectedUser.name }}</div>
                                    <div class="text-sm text-gray-600 dark:text-gray-400">{{ selectedUser.email }}</div>
                                </div>
                                <button 
                                    @click="addMember" 
                                    class="ml-4 bg-green-500 hover:bg-green-600 text-white py-1 px-3 rounded-md text-sm"
                                >
                                    Adicionar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Lista de membros atuais -->
            <div>
                <h2 class="text-lg font-semibold mb-4 text-gray-800 dark:text-white">Membros Atuais</h2>
                <table-component
                    :loading="loading"
                    :headers="[
                        'Nome',
                        'Email',
                        'Impersonate',
                        'Self Authorize',
                        'Ações'
                    ]"
                >
                    <tr v-for="member in members" :key="member.id" class="hover:bg-gray-100 dark:hover:bg-gray-700">
                        <td class="table-cell-sm">
                            <div class="flex items-center">
                                <div class="ml-3">
                                    <p class="text-gray-900 dark:text-white whitespace-no-wrap">
                                        {{ member.name }}
                                    </p>
                                </div>
                            </div>
                        </td>
                        <td class="table-cell-sm">
                            <p class="text-gray-900 dark:text-white whitespace-no-wrap">
                                {{ member.email }}
                            </p>
                        </td>
                        <td class="table-cell-sm">
                            <div class="flex items-center">
                                <label class="inline-flex items-center cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        :checked="member.has_impersonate_permission" 
                                        @change="toggleImpersonate(member)"
                                        class="sr-only peer"
                                    >
                                    <div class="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600 dark:bg-gray-700 dark:peer-checked:bg-green-500"></div>
                                </label>
                            </div>
                        </td>
                        <td class="table-cell-sm">
                            <div class="flex items-center">
                                <label class="inline-flex items-center cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        :checked="member.has_self_authorize_permission" 
                                        @change="toggleSelfAuthorize(member)"
                                        class="sr-only peer"
                                    >
                                    <div class="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 dark:bg-gray-700 dark:peer-checked:bg-blue-500"></div>
                                </label>
                            </div>
                        </td>
                        <td class="table-cell-sm">
                            <button 
                                @click="removeMember(member)" 
                                class="bg-red-500 hover:bg-red-600 text-white py-1 px-2 rounded-md text-xs"
                            >
                                Remover
                            </button>
                        </td>
                    </tr>
                    <tr v-if="members.length === 0">
                        <td colspan="5" class="table-cell-sm text-center text-gray-500 dark:text-gray-400">
                            Nenhum membro encontrado para este setor.
                        </td>
                    </tr>
                </table-component>
            </div>
        </div>
        
        <!-- Modal de confirmação para remover membro -->
        <div v-if="showRemoveConfirmModal" class="fixed inset-0 z-10 overflow-y-auto">
            <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div class="fixed inset-0 transition-opacity" aria-hidden="true">
                    <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                <div class="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    <div class="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div class="sm:flex sm:items-start">
                            <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                <svg class="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                                    Remover Membro
                                </h3>
                                <div class="mt-2">
                                    <p class="text-sm text-gray-500 dark:text-gray-400">
                                        Tem certeza que deseja remover {{ currentMember.name }} como membro deste setor?
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button 
                            @click="confirmRemoveMember" 
                            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                        >
                            Remover
                        </button>
                        <button 
                            @click="showRemoveConfirmModal = false" 
                            class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import TableComponent from "../../components/layouts/table/TableComponent.vue";
import UsersSelect from "../../components/forms/UsersSelect.vue";

export default {
    components: {
        TableComponent,
        UsersSelect
    },
    data() {
        return {
            sectorId: null,
            sectorName: '',
            members: [],
            selectedUser: null,
            currentMember: null,
            showRemoveConfirmModal: false,
            loading: false
        }
    },
    
    mounted() {
        this.sectorId = this.$route.params.role_id;
        this.fetchSectorDetails();
        this.fetchMembers();
    },
    
    methods: {
        fetchSectorDetails() {
            axios.get(`/api/sectors/${this.sectorId}`)
                .then(response => {
                    this.sectorName = response.data.role.name;
                })
                .catch(error => {
                    console.error('Erro ao carregar detalhes do setor:', error);
                    this.$notify({
                        group: 'error',
                        title: 'Erro',
                        text: 'Erro ao carregar detalhes do setor.'
                    }, 4000);
                });
        },
        
        fetchMembers() {
            this.loading = true;
            axios.get(`/api/sectors/${this.sectorId}/members`)
                .then(response => {
                    this.members = response.data.members.map(member => ({
                        ...member,
                        has_impersonate_permission: member.has_impersonate_permission || false,
                        has_self_authorize_permission: member.has_self_authorize_permission || false
                    }));
                    this.loading = false;
                })
                .catch(error => {
                    console.error('Erro ao carregar membros:', error);
                    this.loading = false;
                    this.$notify({
                        group: 'error',
                        title: 'Erro',
                        text: 'Erro ao carregar membros. Tente novamente mais tarde.'
                    }, 4000);
                });
        },
        
        addMember() {
            if (!this.selectedUser) return;
            
            this.loading = true;
            axios.post(`/api/sectors/${this.sectorId}/members`, {
                user_id: this.selectedUser.id
            })
                .then(response => {
                    this.loading = false;
                    this.selectedUser = null;
                    this.fetchMembers();
                    this.$notify({
                        group: 'success',
                        text: 'Membro adicionado com sucesso!'
                    }, 4000);
                })
                .catch(error => {
                    this.loading = false;
                    console.error('Erro ao adicionar membro:', error);
                    this.$notify({
                        group: 'error',
                        title: 'Erro',
                        text: error.response?.data?.message || 'Erro ao adicionar membro. Tente novamente mais tarde.'
                    }, 4000);
                });
        },
        
        toggleImpersonate(member) {
            axios.patch(`/api/sectors/${this.sectorId}/members/${member.id}/impersonate`, {
                can_impersonate: !member.has_impersonate_permission
            })
                .then(response => {
                    member.has_impersonate_permission = response.data.has_impersonate_permission;
                    this.$notify({
                        group: 'success',
                        text: `Impersonate ${!member.has_impersonate_permission ? 'ativada' : 'desativada'} com sucesso!`
                    }, 4000);
                })
                .catch(error => {
                    this.$notify({
                        group: 'error',
                        title: 'Erro',
                        text: error.response?.data?.message || 'Erro ao atualizar permissão de impersonate.'
                    }, 4000);
                });
        },
        
        toggleSelfAuthorize(member) {
            axios.patch(`/api/sectors/${this.sectorId}/members/${member.id}/self-authorize`, {
                can_self_authorize: !member.has_self_authorize_permission
            })
                .then(response => {
                    member.has_self_authorize_permission = response.data.has_self_authorize_permission;
                    this.$notify({
                        group: 'success',
                        text: `Self Authorize ${response.data.has_self_authorize_permission ? 'ativada' : 'desativada'} com sucesso!`
                    }, 4000);
                })
                .catch(error => {
                    this.$notify({
                        group: 'error',
                        title: 'Erro',
                        text: error.response?.data?.message || 'Erro ao atualizar permissão de self authorize.'
                    }, 4000);
                });
        },
        
        removeMember(member) {
            this.currentMember = member;
            this.showRemoveConfirmModal = true;
        },
        
        confirmRemoveMember() {
            this.loading = true;
            
            axios.delete(`/api/sectors/${this.sectorId}/members/${this.currentMember.id}`)
                .then(response => {
                    this.loading = false;
                    this.showRemoveConfirmModal = false;
                    this.fetchMembers();
                    this.$notify({
                        group: 'success',
                        text: 'Membro removido com sucesso!'
                    }, 4000);
                })
                .catch(error => {
                    this.loading = false;
                    this.showRemoveConfirmModal = false;
                    console.error('Erro ao remover membro:', error);
                    this.$notify({
                        group: 'error',
                        title: 'Erro',
                        text: error.response?.data?.message || 'Erro ao remover membro. Tente novamente mais tarde.'
                    }, 4000);
                });
        },
        
        goBack() {
            this.$router.push({ name: 'sectors' });
        }
    }
}
</script> 